import type { QuoteView } from "@contracts/index";
import { multiply } from "@domain/money";
import { formatLongDate, formatMoney, formatQuantity, paragraphs } from "@presentation/format";
import { labelsFor } from "@presentation/labels";

/**
 * Web version of the quote document. Same sections and labels as the PDF
 * (api/src/infrastructure/pdf) so both read the same way.
 */
export function QuoteDocument({ quote }: { quote: QuoteView }) {
  const t = labelsFor(quote.language);
  const money = (cents: number) => formatMoney(cents, quote.currency, quote.language);

  return (
    <article className="quote-doc" lang={quote.language}>
      <header className="quote-doc-head">
        <div>
          <div className="muted small">{t.preparedFor.toUpperCase()}</div>
          <div className="strong">{quote.client.name}</div>
          {quote.client.taxId && (
            <div className="small">
              {t.taxId}: {quote.client.taxId}
            </div>
          )}
        </div>
        <dl className="quote-doc-meta">
          <dt>{t.folio}</dt>
          <dd>{quote.folio}</dd>
          <dt>{t.date}</dt>
          <dd>{formatLongDate(quote.sentAt ?? quote.createdAt, quote.language)}</dd>
          <dt>{t.validUntil}</dt>
          <dd>{formatLongDate(quote.validUntil, quote.language)}</dd>
        </dl>
      </header>

      <h1>{quote.title}</h1>
      {quote.expired && <p className="error strong">{t.expired}</p>}
      {quote.acceptedBy && (
        <p className="notice">
          {t.accepted} · {t.acceptedBy}: {quote.acceptedBy.name} ({quote.acceptedBy.position})
          {quote.acceptedAt ? ` · ${formatLongDate(quote.acceptedAt, quote.language)}` : ""}
        </p>
      )}
      {paragraphs(quote.intro).map((p, i) => (
        <p key={i} className="pre-line">
          {p}
        </p>
      ))}

      <div className="table-wrap">
        <table className="quote-items">
          <thead>
            <tr>
              <th>{t.concept}</th>
              <th className="num">{t.quantity}</th>
              <th className="num">{t.unitPrice}</th>
              <th className="num">{t.amount}</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((item) => {
              const excluded = item.optional && !item.selected;
              return (
                <tr key={item.id} className={excluded ? "muted" : ""}>
                  <td>
                    <div className="strong">{item.concept}</div>
                    {item.description && <div className="muted small pre-line">{item.description}</div>}
                    {item.optional && (
                      <div className="tag">
                        {t.optional}
                        {excluded ? ` · ${t.notIncluded}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="num">{formatQuantity(item.quantity, quote.language)}</td>
                  <td className="num">{money(item.unitPriceCents)}</td>
                  <td className="num">{money(multiply(item.unitPriceCents, item.quantity))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <table className="totals">
        <tbody>
          <tr>
            <th>{t.subtotal}</th>
            <td>{money(quote.totals.subtotalCents)}</td>
          </tr>
          <tr>
            <th>{quote.totals.taxMode === "iva16" ? t.taxIva : t.taxExempt}</th>
            <td>{money(quote.totals.taxCents)}</td>
          </tr>
          <tr className="grand">
            <th>
              {t.total} {quote.currency}
            </th>
            <td>{money(quote.totals.totalCents)}</td>
          </tr>
        </tbody>
      </table>

      <p>{quote.paymentMethod === "shopify" ? t.paymentShopify : t.paymentTransfer}</p>

      {paragraphs(quote.terms).length > 0 && (
        <section>
          <h2>{t.terms}</h2>
          {paragraphs(quote.terms).map((p, i) => (
            <p key={i} className="small pre-line">
              {p}
            </p>
          ))}
        </section>
      )}
    </article>
  );
}
